import React, { useRef } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { Button } from '@pushpay/button';
import Drawer from '@pushpay/drawer';
import { CollapseIcon } from '@pushpay/iconography';
import { clsx } from '@pushpay/styles';
import { useMediaBreakpoint } from '@pushpay/theming';
import Tooltip from '@pushpay/tooltip';
import { useConstant, useCookie } from '@pushpay/utils';
import { SideBarProvider } from './sidebar-context';
import { sidebarCollapsedWidthPx, sidebarExpandedWidthPx, useStyles } from './sidebar-styles';
import { useScrollShadowStyle } from './use-scroll-shadow-style';
export let ExpandStatus;

(function (ExpandStatus) {
  ExpandStatus["Collapsed"] = "collapsed";
  ExpandStatus["Expanded"] = "expanded";
})(ExpandStatus || (ExpandStatus = {}));

let sideBarCounter = 0;

const noOp = () => {};

const ExpanderIcon = animated(CollapseIcon);
export const SideBar = props => {
  const {
    classes: classesProp,
    className: classNameProp,
    collapseText,
    expandText,
    topSection,
    bottomSection,
    animateIcon = false,
    onToggle,
    mobile,
    'data-pp-at-target': targetId
  } = props;
  const classes = useStyles(classesProp);
  const className = clsx(classes.root, classNameProp); // replace with useId

  const panelId = useConstant(() => `sidebar-${sideBarCounter++}`);
  const [expandStatus, setExpandStatus] = useCookie({
    name: '@pushpay/shell/sidebar/expandStatus',
    defaultValue: ExpandStatus.Collapsed,
    expires: 365
  });

  const toggleExpand = () => {
    const newStatus = expandStatus === ExpandStatus.Collapsed ? ExpandStatus.Expanded : ExpandStatus.Collapsed;
    setExpandStatus(newStatus);
    onToggle === null || onToggle === void 0 ? void 0 : onToggle(newStatus);
  };

  const isCollapsed = expandStatus === ExpandStatus.Collapsed;
  const target = isCollapsed ? 'expand' : 'collapse';
  const {
    opacity,
    scaleX,
    width
  } = useSpring({
    opacity: isCollapsed ? 0 : 1,
    scaleX: isCollapsed ? -1 : 1,
    width: isCollapsed ? sidebarCollapsedWidthPx : sidebarExpandedWidthPx
  });

  const renderExpanderContent = () => {
    if (animateIcon) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExpanderIcon, {
        className: clsx(classes.icon, isCollapsed ? classes.iconExpand : classes.iconCollapse),
        "data-pp-at-target": `${targetId}-${target}-icon`,
        displaySize: "large",
        style: {
          scaleX
        }
      }), /*#__PURE__*/React.createElement(animated.span, {
        className: classes.expanderText,
        style: {
          opacity
        }
      }, collapseText));
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CollapseIcon, {
      className: clsx(classes.icon, isCollapsed ? classes.iconExpand : classes.iconCollapse),
      "data-pp-at-target": `${targetId}-${target}-icon`,
      displaySize: "large"
    }), /*#__PURE__*/React.createElement("span", {
      className: clsx(isCollapsed ? classes.expanderTextCollapsed : classes.expanderTextOpen, classes.expanderText)
    }, collapseText));
  };

  const renderExpander = () => /*#__PURE__*/React.createElement(Tooltip, {
    className: clsx(classes.expanderTooltip),
    content: expandText,
    "data-pp-at-target": `${targetId}-expand-tooltip`,
    disabled: !isCollapsed,
    panelSpace: "XXSMALL",
    placement: "right"
  }, /*#__PURE__*/React.createElement(Button, {
    "aria-controls": panelId,
    "aria-expanded": !isCollapsed,
    className: classes.expanderButton,
    "data-pp-at-target": `${targetId}-expand-btn`,
    onClick: toggleExpand,
    type: "button"
  }, renderExpanderContent()));

  const topSectionRef = useRef(null);
  const [topShadowStyle, topListenerRef] = useScrollShadowStyle();
  const [bottomShadowStyle, bottomListenerRef] = useScrollShadowStyle();
  const isTabletAndBelow = useMediaBreakpoint('TABLET_AND_BELOW');
  const content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(animated.div, {
    style: topShadowStyle,
    className: classes.topShadow
  }), /*#__PURE__*/React.createElement("div", {
    ref: topSectionRef,
    className: clsx(classes.topSection, isCollapsed && classes.topSectionCollapsed)
  }, /*#__PURE__*/React.createElement("div", {
    ref: topListenerRef,
    className: classes.scrollListenerTop
  }), topSection, /*#__PURE__*/React.createElement("div", {
    ref: bottomListenerRef,
    className: classes.scrollListenerBottom
  })), /*#__PURE__*/React.createElement(animated.div, {
    style: bottomShadowStyle,
    className: classes.bottomShadow
  }), bottomSection && /*#__PURE__*/React.createElement("div", {
    className: clsx(classes.bottomSection, isCollapsed && classes.bottomSectionCollapsed)
  }, bottomSection));

  if (mobile && isTabletAndBelow) {
    const {
      isOpenMobile,
      closeMobile,
      mobileButtonRef
    } = mobile;
    return /*#__PURE__*/React.createElement(Drawer, {
      placement: "left",
      visible: isOpenMobile,
      onClose: closeMobile,
      closeOnClickOutside: [mobileButtonRef],
      classes: {
        drawer: classes.drawerMobile,
        mask: classes.drawerMask
      }
    }, /*#__PURE__*/React.createElement(SideBarProvider, {
      expandStatus: "preexpanded",
      toggleExpand: noOp,
      topSectionRef: topSectionRef
    }, content));
  }

  return /*#__PURE__*/React.createElement(SideBarProvider, {
    expandStatus: expandStatus,
    toggleExpand: toggleExpand,
    topSectionRef: topSectionRef
  }, /*#__PURE__*/React.createElement(animated.div, {
    id: panelId,
    className: className,
    style: {
      width
    }
  }, renderExpander(), content));
};